import { useFormik } from "formik";
import Toast from "react-native-toast-message";

import React, {
  useEffect,
  useState,
  useMemo,
  useRef,
  useCallback,
} from "react";
import Text from "@/components/shared/AppText";
import { Animated, Platform, Image, BackHandler, View } from "react-native";
import AsyncStorage from "@react-native-async-storage/async-storage";
import { useDebouncedCallback } from "use-debounce";
import ProgressBar from "react-native-progress/Bar";
import analytics from "@/analytics";
import * as Yup from "yup";
import { roles, eventTypes } from "@/components/globalUtils";
import AppActivityIndicator from "@/components/shared/AppActivityIndicator";
import { makeSlug } from "@/components/globalUtils";
import { Review } from "@/components/shared/PayWall";
import moment from "moment-timezone/builds/moment-timezone-with-data-10-year-range.js";
import { TextLink } from "@/components/shared/TextLink";
import { validateAlias } from "@/components/screens/Schemas";
import * as WebBrowser from "expo-web-browser";

import AppInput from "@/components/shared/AppInput";
import AppRadio from "@/components/shared/AppRadio";
import AppTitle from "@/components/shared/AppTitle";
import OnboardingButton from "@/components/shared/OnboardingButton";
import SignUpContainer from "@/components/shared/SignUpContainer";
import AppBrand from "../shared/AppBrand";
import tw from "twrnc";
import {
  peekFundraiserIntent,
  readFundraiserIntent,
  safeWebsite,
  updateFundraiserIntent,
} from "@/components/donations/fundraiserIntent";
import { DONATIONS_ENABLED } from "@/components/donations/config";
import { EventWhenForm } from "@/components/screens/OnBoarding/EventWhenStep";
import {
  whenProblem,
  withoutStaleDate,
} from "@/components/screens/OnBoarding/eventWhen";
import {
  peekFuneralHomeIntent,
  readFuneralHomeIntent,
} from "@/components/funeral/funeralHomeIntent";
import { reviewDate } from "@/components/LoadingScreen";
import { t } from "@/localization";

const roleValues = roles.map((r) => r.value);

// Account/company answers passed by a landing page (e.g. eventlive.pro/nonprofit
// → ?account_type=business&broadcaster_role=nonprofit&company_name=…), either
// in the URL or in the stored fundraiser intent.
export const onboardingPrefill = (source) => {
  const values = {};
  if (!source || typeof source !== "object") return values;
  const pick = (value, max) =>
    typeof value === "string" ? value.trim().slice(0, max) : "";
  const accountType = pick(source.account_type, 20);
  if (["business", "individual"].includes(accountType)) {
    values.account_type = accountType;
  }
  const role = pick(source.broadcaster_role, 40);
  if (roleValues.includes(role)) values.broadcaster_role = role;
  const companyName = pick(source.company_name, 120);
  if (companyName) values.company_name = companyName;
  const companyUrl = safeWebsite(pick(source.company_url, 200));
  if (companyUrl) values.company_url = companyUrl;
  return values;
};

const withoutEmptyValues = (values) =>
  Object.fromEntries(
    Object.entries(values || {}).filter(
      ([, value]) => value !== "" && value !== null && value !== undefined
    )
  );

function checkTitleForTestIntent(title) {
  return /test|prueba/i.test(title) && !/greatest|contest/i.test(title);
}

const images_phone_to_web = {
  funeral: require("@/assets/phone-to-web-funeral.png"),
  birthday: require("@/assets/phone-to-web-birthday.png"),
  wedding: require("@/assets/phone-to-web-wedding.png"),
  school: require("@/assets/phone-to-web-school.png"),
  sport: require("@/assets/phone-to-web-sport.png"),
  other: require("@/assets/phone-to-web-other.png"),
};
const images_make_it_your_own = {
  funeral: require("@/assets/make-it-your-own-funeral.png"),
  birthday: require("@/assets/make-it-your-own-birthday.png"),
  wedding: require("@/assets/make-it-your-own-wedding.png"),
  school: require("@/assets/make-it-your-own-school.png"),
  sport: require("@/assets/make-it-your-own-sport.png"),
  other: require("@/assets/make-it-your-own-other.png"),
};
const images_replay_download = {
  funeral: require("@/assets/replay-download-funeral.png"),
  birthday: require("@/assets/replay-download-birthday.png"),
  wedding: require("@/assets/replay-download-wedding.png"),
  school: require("@/assets/replay-download-school.png"),
  sport: require("@/assets/replay-download-sport.png"),
  other: require("@/assets/replay-download-other.png"),
};

const contentByEventType = {
  wedding: {
    tip1: {
      title: t("Congratulations! 🎉"),
      // The English keeps the line break and indent it has always shown.
      text: t(`Your wedding day is just a click away for your remote guests. It's almost
      as perfect as being there`),
    },
    tip2: {
      title: t("Make it your own!"),
      text: "",
    },
    tip3: {
      title: t("Replay and Download"),
      text: t(
        "Your video is automatically saved in FULL HD resolution. Download it any time, and choose how long guests can watch the replay: 1 month, 1 year or 10 years."
      ),
    },
    review1: {
      text: t(
        "Compared to the $500 we had been quoted from a live streamer who just sets up on camera, this app was far better and so much cheaper. Highly recommend."
      ),
      author: t("Bride review on the App Store"),
      date: "Aug 2, 2022",
    },
  },
  other: {
    tip1: {
      title: t("Let's get started"),
      text: t(
        "Your event is just a click away for your remote guests. It's almost as perfect as being there"
      ),
    },
    tip2: {
      title: t("Customize everything"),
      text: "",
    },
    tip3: {
      title: t("Replay and Download"),
      text: t(
        "Your video is automatically saved in FULL HD resolution. Download it any time, and choose how long guests can watch the replay: 1 month, 1 year or 10 years."
      ),
    },
    review1: {
      text: t(
        "So simple and easy to use that I would recommend it for any of your live event needs. Weddings, ceremonies of any type, even a conference. This is a simple solution that lets you share the event happening in person with people anywhere. Setup and testing makes it easy to know what you need to do on the day of your event and their team is always standing by to help! Such a fantastic product - thank you!"
      ),
      author: t("{author} on the App Store", { author: "b_rowe3" }),
      date: "Oct 17, 2021",
    },
  },

  funeral: {
    tip1: {
      title: t("Our Condolences"),
      text: t("Give your family and friends the opportunity to say goodbye."),
      // image: require(`@/assets/phone-to-web-funeral.png`),
    },
    tip2: {
      title: t("Personalize the Experience"),
      text: t(
        "Share memories, photos, and messages to make the service more personal."
      ),
    },
    tip3: {
      title: t("Replay and Download"),
      text: t(
        "The service is automatically saved in FULL HD resolution. Download any time after and revisit the memories whenever you wish."
      ),
    },
    review1: {
      text: t(
        "This worked flawlessly for livestream a celebration of life for my daughter. We had over 100 people on the day and many have watched on replay. It was easy to set up and I loved the fact that I could test it ahead of the actual event to build confidence that it would work."
      ),
      author: t("{author}, review on the AppStore", { author: "Kipan" }),
      date: "Dec 7, 2023",
    },
  },
  birthday: {
    tip1: {
      title: t("Happy Birthday! 🎂"),
      text: t(
        "Celebrate your special day with friends and family, no matter where they are."
      ),
    },
    tip2: {
      title: t("Make it a Blast!"),
      text: t("Add your personal touch to the celebration with custom details"),
    },
    tip3: {
      title: t("Replay and Download"),
      text: t(
        "Your video is automatically saved in FULL HD resolution. Download any time after and relive the moments whenever you want."
      ),
    },
    review1: {
      text: t(
        "So simple and easy to use that I would recommend it for any of your live event needs. Weddings, ceremonies of any type, even a conference. This is a simple solution that lets you share the event happening in person with people anywhere. Setup and testing makes it easy to know what you need to do on the day of your event and their team is always standing by to help! Such a fantastic product - thank you!"
      ),
      author: t("{author} on the App Store", { author: "b_rowe3" }),
      date: "Oct 17, 2021",
    },
  },
  sport: {
    tip1: {
      title: t("Game On! 🏀"),
      text: t("Bring the excitement of the game to fans everywhere."),
    },
    tip2: {
      title: t("Personalize the experience"),
      text: "",
    },
    tip3: {
      title: t("Replay and Download"),
      text: t(
        "The game is automatically saved in FULL HD resolution. Download any time after and relive the best moments whenever you want."
      ),
    },
    review1: {
      text: t(" Greet affordable easy to use option for streaming an event"),
      author: t("{author} on the App Store", { author: "TheKozo" }),
      date: "Feb 27, 2023",
    },
  },
  school: {
    tip1: {
      title: t("Let's get started!  🎒"),
      text: t("Go live in 5 minutes, and share it with those who matter."),
    },
    tip2: {
      title: t("Personalize the Experience"),
      text: t(
        "Add links, upload documents, write a welcome message and much more to customize the event"
      ),
    },
    tip3: {
      title: t("Replay and Download"),
      text: t(
        "The video is automatically saved in FULL HD resolution. Download any time after to revisit whenever you want."
      ),
    },

    review1: {
      text: t(
        "Just started using EventLive about a week ago and it has been incredible! It's so easy to use and full of features that is lacking in many other apps in this category. The price per live-stream is also very affordable. I evaluated 5 other live-stream platforms and this is by far the best."
      ),
      author: t("{author} on the App Store", { author: "K. Stockham" }),
      date: "Feb 15, 2022",
    },
  },
};

export const OnboardingSchema = Yup.object().shape({
  account_type: Yup.string().oneOf(["individual", "business", "watch"]),
  broadcaster_role: Yup.string().oneOf(roles.map((r) => r.value)),
  event_type: Yup.string().oneOf(eventTypes.map((r) => r.value)),
  role_other: Yup.string(),
  company_name: Yup.string(),
  company_url: Yup.string(),
  title: Yup.string(),
  event_datetime_utc: Yup.date().nullable(),
  event_date_local: Yup.string(),
  event_time_local: Yup.string(),
  event_timezone: Yup.string(),
  days: Yup.number()
    .typeError(t("Please enter number of days"))
    .integer(t("Please enter number of days"))
    .min(1, t("At least 1 day"))
    .max(7, t("7 days max")),
  alias: Yup.string().nullable(),
  // .test("checkAvailability", "Sorry, Already Taken", validateAlias)
  // .matches(
  //   /^[\d\w-_]+$/,
  //   "Only letters, numbers, dashes and underscore allowed."
  // ),
});
export const Start = ({
  title = t("Your all\u2011in\u2011one event live streaming app"),
}) => {
  return (
    <>
      <AppTitle text={title} style={tw`mt-15 mb-5 text-center text-5xl`} />

      <Text style={tw`text-center text-lg text-[#2e3f52]`}>
        {t("24,000+ events streamed")}
      </Text>
      <Text style={tw`text-center p-1`}>⭐️⭐️⭐️⭐️⭐️</Text>
      <Text style={tw`text-center p-1`}>{t("1000+ reviews worldwide")}</Text>
    </>
  );
};
export const AccountTypeForm = ({
  f,
  title,
  individualLabel = t("A personal event"),
  businessLabel = t("Events for my business"),
}) => {
  return (
    <>
      {title && <AppTitle text={title} style={tw`mt-10 mb-5`} />}

      <AppRadio
        highlight
        selected={f.values.account_type === "individual"}
        onSelect={() => f.setFieldValue("account_type", "individual")}
        label={individualLabel}
      />

      <AppRadio
        highlight
        selected={f.values.account_type === "business"}
        onSelect={() => f.setFieldValue("account_type", "business")}
        label={businessLabel}
      />

      <AppRadio
        highlight
        selected={f.values.account_type === "watch"}
        onSelect={() => f.setFieldValue("account_type", "watch")}
        label={t("I want to attend an event")}
      />
    </>
  );
};

export const BusinessRoleForm = ({
  f,
  title = t("Which role describes you best?"),
}) => (
  <>
    <AppTitle text={title} style={tw`mt-10 mb-5`} />

    {roles.map(({ value, label }) => (
      <AppRadio
        highlight
        key={value}
        selected={f.values.broadcaster_role === value}
        onSelect={() => f.setFieldValue("broadcaster_role", value)}
        label={label}
      />
    ))}
    {/* 
    {f.values.broadcaster_role === "other" && (
      <AppInput
        style={{ width: "100%", marginBottom: 23 }}
        onChangeText={f.handleChange("role_other")}
        onBlur={f.handleBlur("role_other")}
        value={f.values.role_other}
        placeholder="Please Specify"
        onSubmitOnEnter={() => f.handleSubmit()}
        errorMessage={
          f.errors.role_other && f.touched.role_other
            ? f.errors.role_other
            : null
        }
      />
    )} */}
  </>
);

export const EventTypeForm = ({
  f,
  title = t("What kind of event would you like to stream?"),
}) => (
  <>
    <AppTitle text={title} style={tw`mt-10 mb-5`} />

    {eventTypes.map(({ value, label, icon }) => (
      <AppRadio
        highlight
        key={value}
        selected={f.values.event_type === value}
        onSelect={() => f.setFieldValue("event_type", value)}
        label={label}
        icon={icon}
      />
    ))}
  </>
);

export const InfoSlide = ({ title, image, text, imageHeight = 240 }) => (
  <>
    <AppTitle text={title} style={tw`mb-0 text-center text-4xl`} />
    <Image
      source={image}
      resizeMode="contain"
      style={tw`w-full h-[${imageHeight}px] mt-2.5`}
    />
    <Text style={tw`text-center mt-5 text-lg leading-7`}>{text}</Text>
  </>
);

// "No date yet": sign up without creating an event and try a free test.
const goToFreeTest = async (values, navigation, from) => {
  const {
    event_datetime_utc,
    event_date_local,
    event_time_local,
    date_source,
    date_quick,
    ...rest
  } = values;
  await AsyncStorage.setItem(
    "onboardingData",
    JSON.stringify({ ...rest, skipEventCreation: true })
  );
  analytics.track("Onboarding Free Test Route", { from });
  navigation.push("SignUpSignIn", { skipEventCreation: true });
};

export const EventTitleForm = ({
  f,
  title = t("Title of Your Event"),
  emptytitle,
  navigation,
}) => {
  return (
    <>
      <AppTitle text={title} style={tw`mt-10 mb-5`} />

      <AppInput
        onChangeText={(v) => {
          f.setFieldValue("alias", makeSlug(v), false);
          f.handleChange("title")(v);
        }}
        onBlur={f.handleBlur("title")}
        value={f.values.title}
        autoFocus
        placeholder={t("Event Title *")}
        onSubmitOnEnter={() => f.handleSubmit()}
        returnKeyType="next"
        returnKeyLabel={t("Continue")}
        enablesReturnKeyAutomatically={true}
        onSubmitEditing={f.handleSubmit}
        errorMessage={f.errors.title && f.touched.title ? f.errors.title : null}
      />

      {(!f.values.title || checkTitleForTestIntent(f.values.title)) && (
        <View>
          <View style={tw`mt-2.5`}>
            {checkTitleForTestIntent(f.values.title) ? (
              <Text>{t('We\'ve noticed "test" in your event title.')}</Text>
            ) : null}

            <Text>{t("Don't have an event scheduled yet? No problem.")}</Text>
            <TextLink
              text={t("Try a free test first")}
              style={tw`mt-2.5`}
              onPress={() => goToFreeTest(f.values, navigation, "title")}
            />
          </View>
        </View>
      )}
    </>
  );
};

export const EventLinkForm = ({ f, title = t("Your Event Page Link") }) => {
  const [linkStatus, setLinkStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const aliasPattern = /^[\d\w-_]+$/;
  const firstCheck = useRef(true);

  const checkAvailability = async () => {
    let isAvailable = false;
    setLoading(true);

    if (!aliasPattern.test(f.values.alias)) {
      setLinkStatus(false);
    } else {
      isAvailable = await validateAlias(f.values.alias);
      if (!isAvailable && firstCheck.current) {
        f.values.alias += Math.floor(1000 + Math.random() * 9000);
        isAvailable = true;
        firstCheck.current = false;
      }
      setLinkStatus(isAvailable);
    }
    setLoading(false);
    return isAvailable;
  };

  const debouncedCheckLinkAvailability = useDebouncedCallback(
    checkAvailability,
    500
  );

  useEffect(() => {
    if (f.values.alias) {
      debouncedCheckLinkAvailability();
    }
  }, [f.values.alias]);

  const i = useRef(null);

  return (
    <>
      <AppTitle text={title} style={tw`mt-10 mb-5`} />
      <Text style={tw`mb-5`}>
        {t(
          "Your event link is all you need to invite the guests. Text it, email it, print on your invitations."
        )}
      </Text>
      <AppInput
        onChangeText={f.handleChange("alias")}
        onBlur={f.handleBlur("alias")}
        value={f.values.alias}
        placeholder={t("your-event-link")}
        label="evt.live/"
        labelStyle={tw`text-right pr-0 font-bold ${
          Platform.OS === "web" ? "pt-0" : "pt-0.5"
        } text-[#3f5b7a]`}
        inputStyle={tw`pl-0`}
        autoCapitalize="none"
      />
      <View
        style={tw`text-center p-10 rounded-2xl items-center justify-center ${
          loading
            ? ""
            : linkStatus
            ? "bg-[rgba(0,255,0,0.4)]"
            : "bg-[rgba(255,0,0,0.4)]"
        }`}
      >
        {loading ? (
          <AppActivityIndicator />
        ) : linkStatus ? (
          <Text>{t("Your link is available!")}</Text>
        ) : !aliasPattern.test(f.values.alias) ? (
          <Text>
            {t("Only english letters, numbers, dashes and underscore allowed.")}
          </Text>
        ) : (
          <Text>
            {t("Sorry, this link is taken. Try adding a few characters")}
          </Text>
        )}
      </View>
    </>
  );
};

export const BusinessPersonalizeForm = ({
  f,
  title = t("Personalize your event pages"),
}) => (
  <>
    {title && <AppTitle text={title} style={tw`mt-10 mb-5`} />}

    <AppInput
      onChangeText={f.handleChange("company_name")}
      onBlur={f.handleBlur("company_name")}
      value={f.values.company_name}
      placeholder={t("Company Name")}
      onSubmitOnEnter={() => f.handleSubmit()}
      errorMessage={
        f.errors.company_name && f.touched.company_name
          ? f.errors.company_name
          : null
      }
    />

    <AppInput
      onChangeText={f.handleChange("company_url")}
      onBlur={f.handleBlur("company_url")}
      value={f.values.company_url}
      placeholder={t("Website (optional)")}
      onSubmitOnEnter={() => f.handleSubmit()}
      errorMessage={
        f.errors.company_url && f.touched.company_url
          ? f.errors.company_url
          : null
      }
    />

    <Text style={tw`mt-5`}>
      {t("You can upload your logo in profile settings")}
    </Text>
  </>
);

export const CreatingEventSlide = ({ f, after, text, author, date }) => {
  useEffect(() => {
    const timer = setTimeout(() => {
      after();
    }, 5000);
    return () => clearTimeout(timer);
  }, [f, after]);

  return (
    <>
      <AppTitle
        text={t("Creating '{title}' event", { title: f.values.title })}
        style={tw`mt-10 mb-5`}
      />
      <AppActivityIndicator />
      {text && author && (
        <Review
          rating={5}
          text={text}
          author={author}
          date={reviewDate(date)}
        />
      )}
    </>
  );
};

const OnboardingNew = ({ navigation, route }) => {
  const {
    first_name,
    account_type,
    company_name,
    company_url,
    broadcaster_role,
    event_type,
    step,
    // finishOnboarding,
  } = route?.params || {};

  // Nonprofits from eventlive.pro/nonprofit already told us their account type,
  // role and organization: welcome them to the fundraiser and go straight to
  // reviewing the organization details.
  const [fundraiserIntent, setFundraiserIntent] = useState(() =>
    DONATIONS_ENABLED ? peekFundraiserIntent() : null
  );
  useEffect(() => {
    if (!DONATIONS_ENABLED) return;
    let active = true;
    readFundraiserIntent().then(
      (intent) => active && setFundraiserIntent(intent)
    );
    return () => {
      active = false;
    };
  }, []);
  const fundraiserFlow =
    !!fundraiserIntent && fundraiserIntent.accountType === "business";

  // Existing customers from an email link sign in instead of starting a new
  // account. Once only, so going back still shows the welcome steps.
  useEffect(() => {
    if (!fundraiserIntent?.existingAccount) return;
    updateFundraiserIntent({ existingAccount: false }).then((intent) => {
      setFundraiserIntent(intent);
      navigation.navigate("SignUpSignIn");
    });
  }, [fundraiserIntent?.existingAccount]);

  // Funeral homes from eventlive.pro/funeral-streaming already gave their
  // funeral home, name and email: skip the questions and create the account.
  // Only from the welcome step, so "back" still offers the questions.
  const [funeralIntent, setFuneralIntent] = useState(() =>
    peekFuneralHomeIntent()
  );
  useEffect(() => {
    let active = true;
    readFuneralHomeIntent().then(
      (intent) => active && setFuneralIntent(intent)
    );
    return () => {
      active = false;
    };
  }, []);
  const funeralRedirected = useRef(false);
  useEffect(() => {
    if (!funeralIntent || funeralRedirected.current) return;
    if (step && step !== "start") return;
    funeralRedirected.current = true;
    navigation.navigate("SignUpSignIn");
  }, [funeralIntent, step]);

  const setStep = (s, params) => {
    if (Platform.OS === "web") {
      // navigation.setParams({ step: s, ...params });
      navigation.navigate("OnboardingNew", { step: s, ...params });
    } else {
      navigation.setParams({ step: s, ...params });
    }
    // navigation.navigate("OnboardingNew", { step: s, ...params });
    // } else {
    //   navigation.push("OnboardingNew", { step: s, ...params });
    // }
  };

  const goBack = useCallback(() => {
    switch (step) {
      case "account_type":
        return setStep("start");
      case "event_type":
        return setStep("account_type");
      case "event_link":
        return setStep("event_datetime");
      case "event_datetime":
        return setStep("event_title");
      case "event_title":
        return setStep("b2c_intro1");
      case "event_link":
        return setStep("event_datetime");
      case "b2c_intro1":
        return setStep("event_type");
      case "b2c_intro3":
        return setStep("event_datetime");
      case "business_role":
        return setStep("account_type");
      case "b2c_intro_download":
        return setStep("event_link");
      case "b2c_intro3":
        return setStep("b2c_intro_download");
      case "business_personalize":
        return setStep("business_role");
      case "creating_event":
        return setStep("b2c_intro_download");
    }
  }, [step]);

  const defaultValues = useMemo(
    () => ({
      account_type: "individual",
      company_name: "",
      company_url: "",
      event_type: "wedding",
      broadcaster_role: "",
      event_timezone: moment.tz.guess(),
      // No pre-filled date: the host picks the day and start time
      // (components/screens/OnBoarding/EventWhenStep.js).
      event_datetime_utc: null,
      event_date_local: "",
      event_time_local: "",
      date_source: "",
      title: "",
      days: 1,
      role_other: "",
      event_name: "",
      alias: "",
    }),
    []
  );

  const [initialValues, setInitialValues] = useState(() => ({
    ...defaultValues,
    ...onboardingPrefill(peekFuneralHomeIntent()?.metadata),
    ...onboardingPrefill(peekFundraiserIntent()?.metadata),
    ...onboardingPrefill(route?.params),
  }));

  useEffect(() => {
    const fetchInitialValues = async () => {
      const storedValues = await AsyncStorage.getItem("onboardingData");
      const intentPrefill = {
        ...onboardingPrefill((await readFuneralHomeIntent())?.metadata),
        ...onboardingPrefill((await readFundraiserIntent())?.metadata),
      };
      const routePrefill = onboardingPrefill(route?.params);
      const hasIntentPrefill = Object.keys(intentPrefill).length > 0;
      if (storedValues) {
        const stored = withoutStaleDate(JSON.parse(storedValues));
        setInitialValues(
          hasIntentPrefill
            ? {
                ...defaultValues,
                ...intentPrefill,
                ...withoutEmptyValues(stored),
                ...routePrefill,
              }
            : { ...stored, ...routePrefill }
        );
      } else if (hasIntentPrefill) {
        setInitialValues({
          ...defaultValues,
          ...intentPrefill,
          ...routePrefill,
        });
      }
    };
    fetchInitialValues();
  }, []);

  useEffect(() => {
    const backAction = () => {
      goBack();
      return true;
    };

    const backHandler = BackHandler.addEventListener(
      "hardwareBackPress",
      backAction
    );

    return () => backHandler.remove();
  }, [step]);

  const f = useFormik({
    initialValues: initialValues,
    validationSchema: OnboardingSchema,
    enableReinitialize: true,
    onSubmit: async (values, actions) => {
      // Trim whitespace in title
      values.title = values.title.trim();
      // Convert alias to lowercase
      values.alias = values.alias.toLowerCase();

      if (step === "event_datetime") {
        if (whenProblem(values)) {
          // The step shows what's missing next to each field's label.
          actions.setSubmitting(false);
          actions.setStatus({ whenTried: true });
          return;
        }
        analytics.track("Event Date Chosen", {
          quick: values.date_quick || "calendar",
          lead_hours: Math.round(
            (new Date(values.event_datetime_utc).getTime() - Date.now()) / 36e5
          ),
          days: Number(values.days) || 1,
        });
      }

      if (step === "event_link") {
        const aliasPattern = /^[\d\w-_]+$/;
        if (!f.values.alias || !aliasPattern.test(f.values.alias)) {
          actions.setSubmitting(false);
          actions.setStatus({ errorMsg: "Invalid link format" });
          Toast.show({
            text1: t(
              "Link must be non-empty and can only contain letters, numbers, dashes and underscores. "
            ),
            text2: t("Please choose another one."),
            type: "error",
            visibilityTime: 5000,
          });
          return;
        }

        const isAvailable = await validateAlias(f.values.alias);
        if (!isAvailable) {
          actions.setSubmitting(false);
          actions.setStatus({ errorMsg: "Link is taken" });
          Toast.show({
            text1: t("Sorry, this link is taken. "),
            text2: t("Please choose another one."),
            type: "error",
            visibilityTime: 5000,
          });
          return;
        }
      }

      // console.log(step, JSON.stringify(values));
      actions.setSubmitting(true);
      console.log(JSON.stringify(values));
      await AsyncStorage.setItem("onboardingData", JSON.stringify(values));

      if (values.account_type) {
        analytics.identifyAnon(values);
      }

      await next(values);

      try {
        // FINAL ACTION:
        //   await updateProfile(values);
        //   await finishOnboarding(values);
      } catch (err) {
        actions.setSubmitting(false);
        actions.setStatus({ errorMsg: err.message });

        Toast.show({
          text1: err.message,
          type: "error",
          visibilityTime: 5000,
        });
      }
    },
  });

  const next = async (values) => {
    switch (step) {
      case "account_type": {
        switch (values.account_type) {
          case "business":
            return setStep("business_role");
          case "individual":
            return setStep("event_type");
          case "watch": {
            analytics.track("Watch Event Intent", {});
            if (Platform.OS !== "web") {
              await WebBrowser.openBrowserAsync("https://evt.live");
            } else {
              window.open("https://evt.live", "_blank");
            }
          }
        }
      }
      case "event_type":
        return setStep("b2c_intro1");
      case "b2c_intro1":
        return setStep("event_title");
      case "event_title":
        if (!values.title || checkTitleForTestIntent(values.title)) {
          return setStep("event_title", { emptytitle: true });
        } else {
          return setStep("event_datetime");
        }

      case "event_datetime":
        return setStep("b2c_intro3");
      case "b2c_intro3":
        return setStep("event_link");
      case "business_role":
        return setStep("business_personalize");
      case "event_link":
        return setStep("b2c_intro_download");
      case "b2c_intro_download":
        return setStep("creating_event");
      case "business_personalize":
      case "creating_event":
        setStep("b2c_intro_download");
        return navigation.navigate("SignUpSignIn");
      default:
        if (
          fundraiserFlow &&
          values.account_type === "business" &&
          values.broadcaster_role
        ) {
          return setStep("business_personalize");
        }
        return setStep("account_type");
    }
  };

  const renderStep = (f) => {
    const event_type = f.values["event_type"] || "other";

    switch (step) {
      default:
      case "start":
        return fundraiserFlow ? (
          <Start
            title={
              fundraiserIntent.orgName
                ? t("Set up a live fundraiser for {name}", {
                    name: fundraiserIntent.orgName,
                  })
                : t("Set up your live fundraiser")
            }
          />
        ) : (
          <Start />
        );

      case "account_type":
        return (
          <AccountTypeForm
            f={f}
            title={
              first_name
                ? t("What would you like to live stream, {name}?", {
                    name: first_name,
                  })
                : t("What would you like to live stream?")
            }
          />
        );
      case "business_role":
        return <BusinessRoleForm f={f} />;
      case "event_type":
        return <EventTypeForm f={f} />;
      case "b2c_intro1":
        return (
          <InfoSlide
            title={contentByEventType[event_type]?.tip1?.title}
            image={images_phone_to_web[event_type]}
            text={contentByEventType[event_type]?.tip1?.text}
          />
        );
      case "b2c_intro3":
        return (
          <InfoSlide
            title={contentByEventType[event_type]?.tip2?.title}
            image={images_make_it_your_own[event_type]}
            text={contentByEventType[event_type]?.tip2?.text}
            imageHeight={320}
          />
        );
      case "b2c_intro_download":
        return (
          <InfoSlide
            title={contentByEventType[event_type]?.tip3?.title}
            image={images_replay_download[event_type]}
            text={contentByEventType[event_type]?.tip3?.text}
          />
        );
      case "event_title":
        return (
          <EventTitleForm f={f} {...route?.params} navigation={navigation} />
        );
      case "event_link":
        return <EventLinkForm f={f} />;
      case "event_datetime":
        return (
          <EventWhenForm
            f={f}
            onNoDate={() => goToFreeTest(f.values, navigation, "date")}
          />
        );
      case "business_personalize":
        return <BusinessPersonalizeForm f={f} />;
      case "creating_event":
        return (
          <CreatingEventSlide
            f={f}
            after={next}
            text={contentByEventType[event_type]?.review1?.text}
            author={contentByEventType[event_type]?.review1?.author}
            date={contentByEventType[event_type]?.review1?.date}
          />
        );
    }
  };

  const progressForStep = {
    account_type: 0.15,
    event_type: 0.36,
    b2c_intro1: 0.38,
    event_title: 0.57,
    event_datetime: 0.74,
    b2c_intro3: 0.78,
    event_link: 0.8,
    b2c_intro_download: 0.95,
    creating_event: 0.99,

    business_role: 0.75,

    business_personalize: 0.95,
  };

  const fadeAnim = useRef(new Animated.Value(0)).current;

  useEffect(() => {
    Animated.timing(fadeAnim, {
      toValue: 1,
      duration: 800,
      useNativeDriver: true,
    }).start();
  }, [step]);

  return (
    <SignUpContainer
      onTouchStart={(e) => {
        if (Platform.OS === "ios") {
          this.touchX = e.nativeEvent.pageX;
        }
      }}
      onTouchEnd={(e) => {
        if (Platform.OS === "ios" && e.nativeEvent.pageX - this.touchX > 70)
          goBack();
      }}
    >
      <View>
        <View style={tw`flex flex-row items-start`}>
          <AppBrand style={tw`mb-5`} />
        </View>

        <ProgressBar
          progress={progressForStep[step] || 0.1}
          color="#4ec8ff"
          width={null}
        />
      </View>
      <View style={tw`flex flex-1 flex-col justify-center`}>
        <Animated.View style={{ opacity: fadeAnim }}>
          {renderStep(f)}
        </Animated.View>
      </View>
      {!["creating_event"].includes(step) && (
        <OnboardingButton
          onPress={f.handleSubmit}
          disabled={f.isSubmitting}
          text={t("Continue")}
        />
      )}
      {!step && (
        <TextLink
          text={t("I already have an account")}
          onPress={() => {
            navigation.navigate("SignUpSignIn");
          }}
          textStyle={tw`m-5 text-center`}
        />
      )}
    </SignUpContainer>
  );
};

export default OnboardingNew;
